<template>
  <span class="user-avatar" :class="`is-${size}`">
    <img v-if="src" class="user-avatar__image" :src="src" :alt="name" />
    <span v-else-if="initials" class="user-avatar__initials">
      {{ initials }}
    </span>
    <i v-else class="fa-solid fa-user" />
  </span>
</template>

<script>
import { computed } from 'vue'

export default {
  name: 'UserAvatar',
  props: {
    src: { type: String, default: null },
    name: { type: String, default: '' },
    size: {
      type: String,
      default: 'medium',
      validator: (v) => ['small', 'medium', 'large'].includes(v),
    },
  },
  setup(props) {
    const initials = computed(() => {
      if (!props.name) return ''
      return props.name
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join('')
    })

    return { initials }
  },
}
</script>

<style>
/* Unscoped — globally registered component. BEM naming prevents collisions. */

.user-avatar {
  --avatar-size: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--avatar-size);
  height: var(--avatar-size);
  min-width: var(--avatar-size);
  border-radius: var(--radius-full, 50%);
  overflow: hidden;
  flex-shrink: 0;
  background: var(--bg-muted);
  color: var(--text-muted);
  font-size: calc(var(--avatar-size) * 0.35);
  line-height: 1;
  user-select: none;
}

/* Fit inside Bulma .icon wrapper */
.icon > .user-avatar {
  --avatar-size: 100%;
}

.user-avatar.is-small {
  --avatar-size: 1.5rem;
}
.user-avatar.is-large {
  --avatar-size: 5rem;
}

.user-avatar__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Force color so <a> ancestors can't override via inheritance.
   Uses a muted primary tone rather than plain grey. */
.user-avatar__initials,
.user-avatar > i {
  color: color-mix(in srgb, var(--primary) 50%, var(--text-muted));
}

.user-avatar__initials {
  font-weight: 400;
  letter-spacing: 0.05em;
}
</style>
